import { useMemo, type ReactNode } from "react"
import Head from "next/head"
import { PublicPageProps } from "@/types/page"
import { Honeybadger } from "@honeybadger-io/react"

import { buildClientAxios } from "@/lib/client-axios"
import { useAmplitudeInit } from "@/lib/hooks/use-amplitude-init"
import { useAppInit } from "@/lib/hooks/use-app-init"
import { useUtmTracking } from "@/lib/hooks/use-utm-tracking"
import { useRouter } from "@/lib/router"
import { UserApi } from "@/lib/user-api"
import { MindtripErrorBoundary } from "@/components/mindtrip-error-boundary"
import { UnhandledErrorComponent } from "@/components/unhandled-error-component"

export type MinimalAppProps = Partial<PublicPageProps>

export default function MinimalApp({
  children,
  ...pageProps
}: MinimalAppProps & {
  children: ReactNode
}) {
  const router = useRouter()
  const userApi = useMemo(() => new UserApi(buildClientAxios()), [])
  const session = pageProps.session

  // Use shared hooks for common initialization
  useAmplitudeInit({
    session,
    organization: pageProps.organization,
    pathname: router.pathname,
    query: router.query,
  })

  useAppInit({
    session,
    supportedBrowser: pageProps.supportedBrowser,
  })

  useUtmTracking({
    session,
    query: router.query,
    userApi,
  })

  return (
    <>
      <Head>
        <title>mindtrip</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>
      <MindtripErrorBoundary
        honeybadger={Honeybadger}
        ErrorComponent={UnhandledErrorComponent}
      >
        {children}
      </MindtripErrorBoundary>
    </>
  )
}
